// Shared utilities for loan dialogs and calculators

export function addYears(date: Date, years: number): Date {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years);
    return d;
}

// Determine if a period defined by from-to is within (<=) 18 months inclusive.
// Returns true if within 18 months, false if over 18 months.
export function isWithin18Months(from?: string | null, to?: string | null): boolean {
    const f = parseDateFlexible(from);
    const t = parseDateFlexible(to);
    if (!f || !t) return true; // default to within when insufficient data
    const fPlus18 = addMonths(new Date(f.getFullYear(), f.getMonth(), f.getDate()), 18);
    // Compare at day precision (t at end-of-day considered within bounds)
    const tEnd = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999);
    return tEnd.getTime() <= fPlus18.getTime();
}

// Compute Year of Assessment string (e.g., "2023/24") from a period end date.
// Rule:
// - If month is Jan–Mar (1–3): YOA = (year-1)/last2(year)
// - If month is Apr–Dec (4–12): YOA = year/last2(year+1)
export function computeYearOfAssessment(periodTo: string | null | undefined): string | null {
    const d = parseAnyDate(periodTo || undefined);
    if (!d) return null;
    const y = d.getFullYear();
    const m = d.getMonth() + 1; // 1..12
    if (m >= 1 && m <= 3) {
        const left = y - 1;
        const right = String(y).slice(-2);
        return `${left}/${right}`;
    }
    const left = y;
    const right = String(y + 1).slice(-2);
    return `${left}/${right}`;
}

export function addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

export function parseYMD(d?: string | null): Date | null {
    if (!d) return null;
    // DD/MM/YYYY
    const m1 = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(d);
    if (m1) {
        const dd = Number(m1[1]);
        const mm = Number(m1[2]) - 1;
        const yy = Number(m1[3]);
        const dt = new Date(yy, mm, dd);
        if (dt.getFullYear() === yy && dt.getMonth() === mm && dt.getDate() === dd) return dt;
    }
    // DD-MMM-YY or DD-MMM-YYYY
    const mShort = /^([0-9]{1,2})-([A-Za-z]{3})-([0-9]{2,4})$/.exec(d);
    if (mShort) {
        const dd = Number(mShort[1]);
        const monMap: Record<string, number> = {
            Jan: 0,
            Feb: 1,
            Mar: 2,
            Apr: 3,
            May: 4,
            Jun: 5,
            Jul: 6,
            Aug: 7,
            Sep: 8,
            Oct: 9,
            Nov: 10,
            Dec: 11
        };
        const monKey = mShort[2].slice(0, 3).replace(/^[a-z]/, c => c.toUpperCase());
        const mm = monMap[monKey] ?? NaN;
        let yy = Number(mShort[3]);
        if (yy < 100) yy = 2000 + yy;
        const dt = new Date(yy, mm, dd);
        if (!Number.isNaN(mm) && dt.getFullYear() === yy && dt.getMonth() === mm && dt.getDate() === dd) return dt;
    }
    // YYYY-MM-DD
    const m2 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (m2) {
        const yy = Number(m2[1]);
        const mm = Number(m2[2]) - 1;
        const dd = Number(m2[3]);
        const dt = new Date(yy, mm, dd);
        if (dt.getFullYear() === yy && dt.getMonth() === mm && dt.getDate() === dd) return dt;
    }
    const t = new Date(d);
    return isNaN(t.getTime()) ? null : t;
}

export function formatDayMonYYYY(d: Date | null): string {
    if (d == null) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const yy = String(d.getFullYear()).slice(-2);
    return `${day} ${mons[d.getMonth()]} ${d.getFullYear()}`;
}

export const isRowFilled = (r: any): boolean => {
    const hasText = (r.invNo || '').trim() !== '' || (r.description || '').trim() !== '';
    const hasDate = !!r.date;
    const amt = toNumber(r.amount);
    const hasAmt = amt !== null && amt !== 0;
    return hasText || hasDate || hasAmt;
};

export function toNumber(v: any): number {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

export function addMonths(date: Date, months: number): Date {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    if (d.getDate() < day) d.setDate(0);
    return d;
}

export function formatDDMMYYYY(date: Date): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

export function formatYYYYMMDD(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

export function parseDDMMYYYY(value?: string | null): Date | null {
    if (!value) return null;
    const m = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(value);
    if (!m) return null;
    const d = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const y = Number(m[3]);
    const dt = new Date(y, mo, d);
    if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d) return dt;
    return null;
}

export function parseYYYYMMDD(value?: string | null): Date | null {
    if (!value) return null;
    const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(value);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d) return dt;
    return null;
}

/** Strict `dd/MM/yyyy` (zero-padded, calendar-valid). */
export function isStrictDdMmYyyy(value?: string | null): boolean {
    return parseDDMMYYYY(value?.trim() ?? null) != null;
}

/** Format Date as MM/YYYY (month-year, day ignored). */
export function formatMMYYYY(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${yyyy}`;
}

/** Parse MM/YYYY (or M/YYYY) → Date at day 1 of that month; invalid → null. */
export function parseMMYYYY(value?: string | null): Date | null {
    if (!value) return null;
    const m = /^([0-9]{1,2})\/([0-9]{4})$/.exec(value.trim());
    if (!m) return null;
    const mo = Number(m[1]) - 1;
    const y = Number(m[2]);
    if (mo < 0 || mo > 11) return null;
    const dt = new Date(y, mo, 1);
    if (dt.getFullYear() === y && dt.getMonth() === mo) return dt;
    return null;
}

/** Bind DatePicker (DD/MM/YYYY) to stored MM/YYYY — use day 01. */
export function monthYearToDdMmYyyy(mmYyyy?: string | null): string | null {
    const d = parseMMYYYY(mmYyyy);
    return d ? formatDDMMYYYY(d) : null;
}

/** Extract MM/YYYY from a DatePicker DD/MM/YYYY value. */
export function ddMmYyyyToMonthYear(ddMmYyyy?: string | null): string {
    const d = parseDDMMYYYY(ddMmYyyy);
    return d ? formatMMYYYY(d) : '';
}

/** Days in month 1–12. Unknown year: February allows 29 until yyyy is complete. */
function getMaxDaysInMonth(month: number, year?: number): number {
    if (month === 2) {
        if (!year) return 29;
        const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        return isLeap ? 29 : 28;
    }
    if ([4, 6, 9, 11].includes(month)) return 30;
    return 31;
}

/** Two-digit year: keep 19/20 as century prefix; otherwise prepend 20. */
function expandTwoDigitYear(year: string): string {
    if (year.length !== 2) return year;
    if ((year[0] === '1' && year[1] === '9') || (year[0] === '2' && year[1] === '0')) {
        return year;
    }
    return `20${year}`;
}

function clampMonthDigits(month: string): string {
    const monthNum = parseInt(month, 10);
    if (monthNum < 1) return '01';
    if (monthNum > 12) return '12';
    return month;
}

/** Cap DD in `DD/` against the month (and optional year). */
function capDayPrefix(result: string, monthNum: number, year?: number): string {
    const dayPart = result.split('/')[0];
    if (dayPart && dayPart.length === 2) {
        const dayNum = parseInt(dayPart, 10);
        const maxDays = getMaxDaysInMonth(monthNum, year);
        if (dayNum > maxDays) {
            return `${maxDays.toString().padStart(2, '0')}/`;
        }
    }
    return result;
}

/**
 * Mask typed text as dd/MM/yyyy.
 * `/` completes a 1-digit day/month unless that digit is 0 (slash ignored).
 * Two-digit years not starting 19/20 get a 20 prefix; Feb days re-checked on leap year.
 */
export function formatDateInput(value: string): string {
    let cleaned = value.replace(/[^\d/]/g, '');
    const slashes = cleaned.split('/').length - 1;
    if (slashes > 2) {
        const partsAll = cleaned.split('/');
        cleaned = `${partsAll[0]}/${partsAll[1]}/${partsAll.slice(2).join('')}`;
    }
    const parts = cleaned.split('/');
    let result = '';

    if (parts[0]) {
        let day = parts[0].replace(/\D/g, '');
        if (day.length > 2) day = day.slice(0, 2);
        if (day.length === 1 && day === '0' && parts.length > 1) {
            return '0';
        }
        if (day.length === 1 && (parseInt(day, 10) > 3 || parts.length > 1)) {
            day = `0${day}`;
            result = `${day}/`;
        } else if (day.length === 2) {
            const dayNum = parseInt(day, 10);
            if (dayNum < 1) day = '01';
            else if (dayNum > 31) day = '31';
            result = day;
            if (cleaned.indexOf('/') === -1 && cleaned.length >= 2) {
                result += '/';
            } else if (parts.length > 1) {
                result += '/';
            }
        } else {
            result = day;
        }
    }

    if (parts.length > 1 && parts[1] !== undefined) {
        let month = parts[1].replace(/\D/g, '');
        if (month.length > 2) month = month.slice(0, 2);
        if (month.length === 1 && month === '0' && parts.length > 2) {
            return `${result}0`;
        }
        if (month.length === 1 && (parseInt(month, 10) > 1 || parts.length > 2)) {
            month = `0${month}`;
            const monthNum = parseInt(month, 10);
            result = capDayPrefix(result, monthNum);
            result += `${month}/`;
        } else if (month.length === 2) {
            month = clampMonthDigits(month);
            const monthNum = parseInt(month, 10);
            result = capDayPrefix(result, monthNum);
            result += month;
            if (cleaned.split('/').length < 3 && cleaned.length >= 5) {
                result += '/';
            } else if (parts.length > 2) {
                result += '/';
            }
        } else {
            result += month;
        }
    }

    if (parts.length > 2 && parts[2] !== undefined) {
        let year = parts[2].replace(/\D/g, '');
        if (year.length > 4) year = year.slice(0, 4);
        if (year.length === 2) {
            year = expandTwoDigitYear(year);
        }
        result += year;
        if (year.length === 4) {
            const [dayStr, monthStr] = result.split('/');
            if (dayStr && monthStr && dayStr.length === 2 && monthStr.length === 2) {
                const dayNum = parseInt(dayStr, 10);
                const monthNum = parseInt(monthStr, 10);
                const yearNum = parseInt(year, 10);
                const maxDays = getMaxDaysInMonth(monthNum, yearNum);
                if (dayNum > maxDays) {
                    result = `${maxDays.toString().padStart(2, '0')}/${monthStr}/${year}`;
                }
            }
        }
    }

    return result;
}

/**
 * Mask typed text as MM/YYYY.
 * Month 2–9 auto-pads; `/` completes 1–9 except a leading 0; two-digit years expand like day picker.
 */
export function formatMonthYearInput(value: string): string {
    let cleaned = value.replace(/[^\d/]/g, '');
    const slashCount = cleaned.split('/').length - 1;
    if (slashCount > 1) {
        const partsAll = cleaned.split('/');
        cleaned = `${partsAll[0]}/${partsAll.slice(1).join('')}`;
    }
    if (!cleaned.includes('/') && cleaned.length > 2) {
        cleaned = `${cleaned.slice(0, 2)}/${cleaned.slice(2, 6)}`;
    }
    const parts = cleaned.split('/');
    let month = (parts[0] || '').replace(/\D/g, '').slice(0, 2);
    if (month.length === 1 && month === '0' && parts.length > 1) {
        return '0';
    }
    if (month.length === 1 && (parseInt(month, 10) > 1 || parts.length > 1)) {
        month = `0${month}`;
    }
    let result = '';
    if (month.length === 2) {
        month = clampMonthDigits(month);
        result = month;
        if (cleaned.includes('/') || (parts[0] || '').length >= 2) result += '/';
    } else {
        result = month;
    }
    if (parts.length > 1 && parts[1] !== undefined) {
        let year = parts[1].replace(/\D/g, '').slice(0, 4);
        if (year.length === 2) {
            year = expandTwoDigitYear(year);
        }
        result += year;
    }
    return result;
}

export const DATE_FORMAT_HINT = 'Please use dd/MM/yyyy format';
export const MONTH_YEAR_FORMAT_HINT = 'Please use MM/YYYY format';

/**
 * Normalize common date strings to strict `dd/MM/yyyy`.
 * Accepts dd/MM/yyyy, d/M/yyyy, yyyy-MM-dd, yyyy/MM/dd, dd-MM-yyyy (and with time suffix).
 * Does not use native Date parsing (avoids MM/DD ambiguity).
 */
export function normalizeToDdMmYyyy(value?: string | null): string | null {
    if (value == null) return null;
    let s = String(value).trim();
    if (!s) return null;
    const tIdx = s.indexOf('T');
    if (tIdx > 0) s = s.slice(0, tIdx).trim();
    else {
        const spaceIdx = s.indexOf(' ');
        if (spaceIdx > 0 && /^\d/.test(s)) s = s.slice(0, spaceIdx).trim();
    }

    const asYmd = (d: number, mo0: number, y: number): string | null => {
        const dt = new Date(y, mo0, d);
        if (dt.getFullYear() === y && dt.getMonth() === mo0 && dt.getDate() === d) {
            return formatDDMMYYYY(dt);
        }
        return null;
    };

    const slashDmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashDmy) {
        return asYmd(Number(slashDmy[1]), Number(slashDmy[2]) - 1, Number(slashDmy[3]));
    }
    const iso = parseYYYYMMDD(s);
    if (iso) return formatDDMMYYYY(iso);
    const slashYmd = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (slashYmd) {
        return asYmd(Number(slashYmd[3]), Number(slashYmd[2]) - 1, Number(slashYmd[1]));
    }
    const dashDmy = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dashDmy) {
        return asYmd(Number(dashDmy[1]), Number(dashDmy[2]) - 1, Number(dashDmy[3]));
    }
    return null;
}

/**
 * Convert date string from yyyy-MM-dd format to dd/MM/yyyy format.
 * If the input is already in dd/MM/yyyy format, returns as is.
 * @param dateStr - Date string to convert
 * @returns Converted date string in dd/MM/yyyy format
 */
export function convertToDisplayFormat(dateStr: string): string {
    if (!dateStr) return '';
    return normalizeToDdMmYyyy(dateStr) || dateStr;
}

/**
 * Convert date string from dd/MM/yyyy format to yyyy-MM-dd format.
 * If the input is already in yyyy-MM-dd format, returns as is.
 * @param dateStr - Date string to convert
 * @returns Converted date string in yyyy-MM-dd format
 */
export function convertToISOFormat(dateStr: string): string {
    if (!dateStr) return '';
    // If already in yyyy-MM-dd format, return as is
    if (dateStr.includes('-')) return dateStr;
    // Try to parse as dd/MM/yyyy and convert
    const date = parseDDMMYYYY(dateStr);
    if (date) return formatYYYYMMDD(date);
    return dateStr;
}

export function periodOrYear(start: string, end: string) {
    if (!start) return 'the period';
    if (!end) return 'the period';
    const startParse = parseYMD(start);
    const endParse = parseYMD(end);
    const dayCount = daysDiffInclusive(endParse, startParse);
    return dayCount >= 365 ? 'the year' : 'the period'
}

export const daysDiffInclusive = (end: Date | null, start: Date | null): number => {
    if (end === null || start === null) return 0;
    const start0 = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const end0 = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const ms = end0.getTime() - start0.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
    return days < 0 ? 0 : days;
};

export function parseAnyDate(value?: string | null): Date | null {
    if (!value) return null;
    const ymd = parseYYYYMMDD(value);
    if (ymd) return ymd;
    return parseDDMMYYYY(value);
}

export function inRange(dateStr: string, from?: string | null, to?: string | null): boolean {
    const d = parseAnyDate(dateStr || undefined);
    if (!d) return false;
    const hasFrom = !!from && String(from).trim() !== '';
    const hasTo = !!to && String(to).trim() !== '';
    if (!hasFrom && !hasTo) return false;
    const f = parseAnyDate(hasFrom ? from! : undefined);
    const t = parseAnyDate(hasTo ? to! : undefined);
    if (f && d < f) return false;
    if (t) {
        const te = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999);
        if (d > te) return false;
    }
    return true;
}

export function formatToday(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

// ===== Shared FS utilities (BalanceSheet / ProfitLoss) =====

// Safely parse amount strings like "1,234.56" into number.
export function parseAmountDisplay(input: string | number | null | undefined): number {
    if (input === null || input === undefined) return 0;
    if (typeof input === 'number') return Number.isFinite(input) ? input : 0;
    const cleaned = String(input).replace(/,/g, '').trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
}

// Format number-like input into localized string with 2 fraction digits.
export function formatAmountDisplay(input: string | number | null | undefined, options?: Intl.NumberFormatOptions): string {
    const n = typeof input === 'number' ? input : parseAmountDisplay(input);
    const fmt: Intl.NumberFormatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2, ...(options || {}) };
    return Number(n).toLocaleString(undefined, fmt);
}

/** Total-row display: negatives as accounting parentheses, e.g. (1,234.56). */
export function formatAmountWithParen(input: string | number | null | undefined, options?: Intl.NumberFormatOptions): string {
    const n = typeof input === 'number' ? input : parseAmountDisplay(input);
    const absFormatted = formatAmountDisplay(Math.abs(Number(n) || 0), options);
    return (Number(n) || 0) < 0 ? `(${absFormatted})` : absFormatted;
}

/**
 * Footer total formatting. Opt into parentheses only for DIR/EXG expanded paths;
 * default keeps legacy toLocaleString (no paren) for other CommonTable/CommonCompareTable callers.
 */
export function formatFooterAmount(
    input: string | number | null | undefined,
    useParen?: boolean,
    options?: Intl.NumberFormatOptions,
): string {
    return useParen ? formatAmountWithParen(input, options) : formatAmountDisplay(input, options);
}

// Flexible date parser supporting DD/MM/YYYY, YYYY-MM-DD, YYYY/MM/DD and native ISO formats.
export function parseDateFlexible(input?: string | null): Date | null {
    if (!input) return null;
    const s = String(input).trim();
    // Try strict internal formats first
    const any = parseAnyDate(s);
    if (any) return any;
    // YYYY/MM/DD
    const m2 = s.match(/^([0-9]{4})\/(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])$/);
    if (m2) {
        const y = Number(m2[1]);
        const mo = Number(m2[2]) - 1;
        const d = Number(m2[3]);
        const dt = new Date(y, mo, d);
        return isNaN(dt.getTime()) ? null : dt;
    }
    // Fallback to native Date
    const nat = new Date(s);
    return isNaN(nat.getTime()) ? null : nat;
}

// Format a date string as "D Month YYYY" (e.g., "31 March 2024").
export function formatAsAtDate(input?: string | null): string {
    const d = parseDateFlexible(input);
    if (!d) return input || '';
    const day = d.getDate();
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
}

// Whole-day difference (inclusive) between two date strings.
export function daysBetween(from?: string | null, to?: string | null): number | null {
    if (!from || !to) return null;
    const a = parseDateFlexible(from);
    const b = parseDateFlexible(to);
    if (!a || !b) return null;
    const aUTC = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const bUTC = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    const ONE = 24 * 60 * 60 * 1000;
    return Math.round((bUTC - aUTC) / ONE) + 1; // inclusive
}

// ===== Common totals calculator for CommonTable-like rows =====
// Rows expected to contain: perClient, adjustments (array), lastYearFinal
export type CommonLikeRow = {
    perClient: any;
    adjustments?: any;
    lastYearFinal: any;
};

export function computeCommonTotals<T extends CommonLikeRow>(rows: T[] | null | undefined) {
    let perClient = 0;
    let adjustment = 0;
    let final = 0;
    let perCheckFinal = 0;
    let lastYearFinal = 0;
    (rows || []).forEach((r) => {
        const pc = toNumber((r as any).perClient);
        const adjustments = (r as any).adjustments;
        // 同时考虑 adjustments 数组（后端数据）和 adjustment 单值（用户输入）
        // 优先使用 adjustments 数组，如果没有则使用 adjustment 单值
        const adj = Array.isArray(adjustments) && adjustments.length > 0
            ? adjustments.reduce((sum: number, d: any) => sum + (Number(d?.adjustment) || 0), 0)
            : (Number((r as any).adjustment) || 0);
        // perCheckFinal: 仅使用后端 adjustments 数组，不包含用户输入的 adjustment 单值
        const backendAdj = Array.isArray(adjustments) && adjustments.length > 0
            ? adjustments.reduce((sum: number, d: any) => sum + (Number(d?.adjustment) || 0), 0)
            : 0;
        perClient += pc;
        adjustment += adj;
        final += pc + adj;
        perCheckFinal += pc + backendAdj;
        lastYearFinal += toNumber((r as any).lastYearFinal);
    });
    return {perClient, adjustment, final, perCheckFinal, lastYearFinal};
}

export function parseNumber(input: number | string | null | undefined): number {
    if (typeof input === 'number') return input;
    if (typeof input === 'string') {
        const trimmed = input.trim();
        const isParenNegative = /^\(.*\)$/.test(trimmed);
        const cleaned = trimmed
            .replace(/[(),]/g, '')
            .replace(/\s+/g, '')
            .trim();
        const n = Number(cleaned);
        if (!Number.isFinite(n)) return 0;
        return isParenNegative ? -Math.abs(n) : n;
    }
    return 0;
}

// ===== Loan Calculator Utilities =====

/**
 * Calculate monthly loan payment using equal installment formula.
 * @param principal - Loan amount (P)
 * @param annualRatePercent - Annual interest rate as percentage (e.g., 5 for 5%)
 * @param termMonths - Number of monthly payments (n)
 * @returns Monthly payment amount, or empty string if inputs are invalid
 */
export function calculateMonthlyPayment(principal: number, annualRatePercent: number, termMonths: number): string {
    const P = toNumber(principal);
    const rAnnual = toNumber(annualRatePercent) / 100;
    const n = termMonths ?? 0;
    if (!P || !n) return '';
    // Convert annual rate to monthly rate
    const r = rAnnual / 12;
    if (r === 0) return formatNumber(P / n);
    // Equal monthly installment formula: P * r / (1 - (1 + r)^-n)
    const payment = (P * r) / (1 - Math.pow(1 + r, -n));
    return formatNumber(payment);
}

/**
 * Format a number with 2 decimal places and thousand separators.
 * @param value - Number to format
 * @returns Formatted string (e.g., "1,234.56")
 */
export function formatNumber(value: number): string {
    return (Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

/**
 * Format a number with 4 decimal places (for interest rates).
 * @param value - Number to format
 * @returns Formatted string (e.g., "3.3333")
 */
export function formatNumber4Decimals(value: number): string {
    return (Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
    });
}

/**
 * Format a date range as "YYYY/YY" year span.
 * @param from - Start date
 * @param to - End date
 * @returns Formatted year span (e.g., "2023/24"), or "-" if dates are invalid
 */
export function formatYearSpan(from?: Date | null, to?: Date | null): string {
    if (!from || !to) return '-';
    const y1 = from.getFullYear();
    const y2 = to.getFullYear();
    const tail = String(y2).slice(-2);
    return `${y1}/${tail}`;
}

/**
 * Sum loan schedule payments within a date range (inclusive).
 * @param schedule - Array of loan schedule items with repaymentDate and payment field
 * @param field - Field to sum ('interestPayment' or 'principalPayment')
 * @param from - Start date (inclusive)
 * @param to - End date (inclusive, extends to end of day)
 * @returns Sum of payments in range, or 0 if no data
 */
export function sumByRange(
    schedule: Array<{repaymentDate: string; [key: string]: any}> | null | undefined,
    field: string,
    from?: Date | null,
    to?: Date | null
): number {
    if (!schedule || !schedule.length || !from || !to) return 0;
    const te = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
    return schedule.reduce((acc, r) => {
        const d = parseAnyDate(r.repaymentDate);
        if (!d) return acc;
        if (d >= from && d <= te) return acc + (r[field] || 0);
        return acc;
    }, 0);
}

/**
 * Sum loan schedule payments after a specific date.
 * @param schedule - Array of loan schedule items with repaymentDate and payment field
 * @param field - Field to sum ('principalPayment' or 'interestPayment')
 * @param after - Date threshold (exclusive)
 * @returns Sum of payments after date, or 0 if no data
 */
export function sumAfterDate(
    schedule: Array<{repaymentDate: string; [key: string]: any}> | null | undefined,
    field: string,
    after?: Date | null
): number {
    if (!schedule || !schedule.length || !after) return 0;
    return schedule.reduce((acc, r) => {
        const d = parseAnyDate(r.repaymentDate);
        if (!d) return acc;
        if (d > after) return acc + (r[field] || 0);
        return acc;
    }, 0);
}

/**
 * Get the last outstandingBalance value within a date range (inclusive).
 * @param schedule - Array of loan schedule items with repaymentDate and outstandingBalance
 * @param from - Start date (inclusive)
 * @param to - End date (inclusive, extends to end of day)
 * @returns Last outstandingBalance in range, or 0 if no data
 */
export function getLastBalanceInRange(
    schedule: Array<{repaymentDate: string; outstandingBalance: number; [key: string]: any}> | null | undefined,
    from?: Date | null,
    to?: Date | null
): number {
    if (!schedule || !schedule.length || !from || !to) return 0;
    const te = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
    let lastBalance = 0;
    let lastDate: Date | null = null;
    
    schedule.forEach(r => {
        const d = parseAnyDate(r.repaymentDate);
        if (!d) return;
        if (d >= from && d <= te) {
            if (!lastDate || d >= lastDate) {
                lastDate = d;
                lastBalance = r.outstandingBalance || 0;
            }
        }
    });
    
    return lastBalance;
}

/**
 * Instalment numbers whose repaymentDate falls in [from, to] (same rules as inRange).
 * Empty / missing window → [].
 */
export function instalmentNosInDateRange(
    schedule: Array<{repaymentDate: string; instalmentNo: number; [key: string]: any}> | null | undefined,
    from?: string | null,
    to?: string | null,
): number[] {
    if (!schedule || !schedule.length) return [];
    const nos: number[] = [];
    schedule.forEach((r) => {
        if (inRange(r.repaymentDate, from, to)) nos.push(r.instalmentNo);
    });
    return nos;
}

/**
 * Sum a numeric schedule field for the given instalment numbers (highlight windows).
 * Empty nos → 0.
 */
export function sumScheduleFieldByInstalmentNos(
    schedule: Array<{instalmentNo: number; [key: string]: any}> | null | undefined,
    nos: number[],
    field: string,
): number {
    if (!schedule || !schedule.length || !nos.length) return 0;
    const want = new Set(nos);
    return schedule.reduce((acc, r) => (
        want.has(r.instalmentNo) ? acc + (Number(r[field]) || 0) : acc
    ), 0);
}

/**
 * outstandingBalance of the last instalment in `nos` (schedule order of nos).
 * Empty nos → 0.
 */
export function getLastBalanceByInstalmentNos(
    schedule: Array<{instalmentNo: number; outstandingBalance?: number; [key: string]: any}> | null | undefined,
    nos: number[],
): number {
    if (!schedule || !schedule.length || !nos.length) return 0;
    const lastNo = nos[nos.length - 1];
    const row = schedule.find((r) => r.instalmentNo === lastNo);
    const n = Number(row?.outstandingBalance);
    return Number.isFinite(n) ? n : 0;
}

// ===== Rental & Date Utilities =====

/**
 * Calculate the number of months between two dates (inclusive).
 * @param from - Start date
 * @param to - End date
 * @returns Number of months between dates (inclusive), minimum 0
 */
export function monthsDiffInclusive(from: Date, to: Date): number {
    // Count whole months between two dates inclusive of start month if from <= to
    const y = to.getFullYear() - from.getFullYear();
    const m = to.getMonth() - from.getMonth();
    const base = y * 12 + m;
    // if day of 'to' >= day of 'from', count as full month, else reduce by 1
    const adjust = to.getDate() >= from.getDate() ? 1 : 0;
    return Math.max(0, base + adjust);
}

/**
 * Format date as "Mon-YY" (e.g., "Jan-24").
 * @param d - Date to format
 * @returns Formatted string
 */
export function formatMonYY(d: Date): string {
    const mons = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const yy = String(d.getFullYear()).slice(-2);
    return `${mons[d.getMonth()]}-${yy}`;
}

/**
 * Generate an array of months between two dates (inclusive).
 * @param aFrom - Start date
 * @param aTo - End date
 * @returns Array of Date objects representing the first day of each month in range
 */
export function monthsBetweenInclusive(aFrom: Date, aTo: Date): Date[] {
    if (aFrom > aTo) return [];
    const start = new Date(aFrom.getFullYear(), aFrom.getMonth(), 1);
    const end = new Date(aTo.getFullYear(), aTo.getMonth(), 1);
    const out: Date[] = [];
    for (let d = new Date(start); d <= end; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
        out.push(new Date(d));
    }
    return out;
}

/**
 * Calculate overlapping months between two date ranges (inclusive).
 * @param fromA - Start date of range A
 * @param toA - End date of range A
 * @param fromB - Start date of range B
 * @param toB - End date of range B
 * @returns Number of overlapping months
 */
export function overlapMonthsInclusive(fromA: Date, toA: Date, fromB: Date, toB: Date): number {
    const start = fromA > fromB ? fromA : fromB;
    const end = toA < toB ? toA : toB;
    if (start > end) return 0;
    return monthsDiffInclusive(start, end);
}

/**
 * 计算租金金额，最后一个月精确到日。
 * 如果结束日期不是当月最后一天，则最后一个月按 月租 / 当月总天数 * 实际天数 计算。
 * @param from - 起始日期
 * @param to - 结束日期
 * @param monthlyRent - 月租金额
 * @returns 租金总额
 */
export function computeRentProrated(from: Date, to: Date, monthlyRent: number): number {
    if (from > to) return 0;

    // 判断结束日期是否是当月最后一天
    const daysInLastMonth = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
    const isLastDayOfMonth = to.getDate() === daysInLastMonth;

    if (isLastDayOfMonth) {
        // 所有月份都是完整月，使用现有逻辑
        return monthlyRent * monthsDiffInclusive(from, to);
    }

    // 最后一个月是不完整的，需要按日计算
    const lastMonthStart = new Date(to.getFullYear(), to.getMonth(), 1);

    if (from >= lastMonthStart) {
        // 起始和结束在同一个月
        const days = to.getDate() - from.getDate() + 1;
        return monthlyRent * days / daysInLastMonth;
    }

    // 计算最后一个月之前的完整月份
    const prevMonthEnd = new Date(to.getFullYear(), to.getMonth(), 0); // 上个月最后一天
    const fullMonths = monthsDiffInclusive(from, prevMonthEnd);
    const fullAmount = monthlyRent * fullMonths;

    // 最后一个月按日计算
    const partialDays = to.getDate();
    const partialAmount = monthlyRent / daysInLastMonth * partialDays;

    return fullAmount + partialAmount;
}

/**
 * Format a date as 'yyyy-MM-DD HH:mm:ss'
 * @param date - Date to format (defaults to current date if not provided)
 * @returns Formatted date string
 */
export function formatDateTime(date?: Date | null): string {
    const d = date || new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
}
